import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addCartItem as addServerCartItem,
  clearServerCart,
  getCart as getServerCart,
  removeCartItem as removeServerCartItem,
  updateCartItem as updateServerCartItem,
} from "../core/services/cartService";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { activeVariants } from "../shared/variantUtils";

const CartContext = createContext(null);
const CART_KEY = "sugamStoreGuestCart";
let syncGeneration = 0;

const readGuestCart = () => {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
  } catch {
    return [];
  }
};

const productIdOf = (item) => {
  if (!item) return "";
  const product = item.product;
  if (typeof product === "string" && product) return product;
  if (product && typeof product === "object") return String(product._id || product.id || "");
  if (item.productId) return String(item.productId);
  return String(item._id || item.id || "");
};

const variantIdOf = (item) => String(item?.variantId || "");
const colorIdOf = (item) => String(item?.colorId || "");
const cartItemKeyOf = (item) => `${productIdOf(item)}:${variantIdOf(item)}:${colorIdOf(item)}`;

const normalizeItem = (item) => {
  const productId = productIdOf(item);
  const product =
    item?.product && typeof item.product === "object" ? item.product : { _id: productId };
  return {
    product,
    productId,
    variantId: variantIdOf(item),
    variantName: item?.variantName || "",
    colorId: colorIdOf(item),
    colorName: item?.colorName || "",
    compareAtPrice: Number(item?.compareAtPrice || 0),
    key: cartItemKeyOf(item),
    name: item?.name || product?.name || "Product",
    price: Number(item?.price ?? product?.sellingPrice ?? product?.price ?? 0),
    quantity: Number(item?.quantity || 1),
    image: item?.image || product?.images?.[0] || "",
    stock: Number(item?.stock ?? product?.stock ?? 0),
  };
};

export const cartItemsOf = (cart) => (cart?.items || []).map(normalizeItem);
export const cartSubtotal = (cart) =>
  cartItemsOf(cart).reduce((sum, item) => sum + item.price * item.quantity, 0);
export const cartCount = (cart) =>
  cartItemsOf(cart).reduce((sum, item) => sum + item.quantity, 0);

export function CartProvider({ children }) {
  const { token } = useAuth();
  const { notify } = useToast();
  const [cart, setCart] = useState(() => ({
    items: localStorage.getItem("token") ? [] : readGuestCart(),
  }));
  const [cartSyncing, setCartSyncing] = useState(() =>
    Boolean(localStorage.getItem("token")),
  );
  const mergeGuestOnLogin = useRef(false);
  const previousToken = useRef(token);

  useEffect(() => {
    if (token) return;
    localStorage.setItem(CART_KEY, JSON.stringify(cart.items || []));
  }, [cart, token]);

  useEffect(() => {
    if (!token) {
      if (previousToken.current) {
        localStorage.removeItem(CART_KEY);
        setCart({ items: [] });
      }
      previousToken.current = "";
      mergeGuestOnLogin.current = true;
      setCartSyncing(false);
      return undefined;
    }
    previousToken.current = token;
    const generation = ++syncGeneration;
    const guestItems = mergeGuestOnLogin.current ? readGuestCart() : [];
    mergeGuestOnLogin.current = false;
    localStorage.removeItem(CART_KEY);
    let cancelled = false;
    const syncCart = async () => {
      setCartSyncing(true);
      try {
        const data = await getServerCart();
        let serverCart = data.cart || { items: [] };
        for (const item of guestItems) {
          const id = productIdOf(item);
          if (!id) continue;
          try {
            const result = await addServerCartItem(
              id,
              Number(item.quantity || 1),
              variantIdOf(item),
              colorIdOf(item),
            );
            serverCart = result.cart || serverCart;
          } catch {
            // Invalid guest items should not block the signed-in cart.
          }
        }
        if (!cancelled && generation === syncGeneration) setCart(serverCart);
      } catch (error) {
        if (!cancelled) notify(error.message, "error");
      } finally {
        if (!cancelled) setCartSyncing(false);
      }
    };
    void syncCart();
    return () => {
      cancelled = true;
    };
  }, [token, notify]);

  const addToCart = useCallback(async (product, quantity = 1, variantId = "", colorId = "") => {
    const productId = productIdOf(product);
    if (!productId) throw new Error("This product cannot be added right now.");
    const variants = activeVariants(product);
    if (variants.length && !variantId) {
      throw new Error("Choose a variant on the product page first.");
    }
    const variant = variants.find((item) => String(item._id) === String(variantId));
    const colors = variant?.colors || [];
    const color = colors.find((item) => String(item._id) === String(colorId));
    if (colors.length && !color) {
      throw new Error("Choose a color on the product page first.");
    }
    if (token) {
      const data = await addServerCartItem(productId, quantity, variantId, colorId);
      setCart(data.cart || { items: [] });
      return;
    }
    if (variants.length && !variant) {
      throw new Error("Choose a valid product variant.");
    }
    const stock = color
      ? Number(color.stock)
      : variant
        ? Number(variant.stock)
        : Number(product.stock || 0);
    const price = variant ? Number(variant.price) : Number(product.sellingPrice > 0 ? product.sellingPrice : product.price || 0);
    const compareAt = variant
      ? Number(variant.compareAtPrice) > price ? Number(variant.compareAtPrice) : 0
      : Number(product.price) > price ? Number(product.price) : 0;
    if (stock < 1) throw new Error("This product is out of stock.");
    const lineKey = `${productId}:${variantId}:${colorId}`;
    const currentItems = cart.items || [];
    const existing = currentItems.find((item) => cartItemKeyOf(item) === lineKey);
    const nextQuantity = Number(existing?.quantity || 0) + quantity;
    if (nextQuantity > stock) throw new Error(`Only ${stock} available in stock.`);
    setCart((current) => {
      const items = [...(current.items || [])];
      const index = items.findIndex((item) => cartItemKeyOf(item) === lineKey);
      if (index >= 0) {
        items[index] = {
          ...items[index],
          quantity: nextQuantity,
          price,
          stock,
          compareAtPrice: compareAt,
          variantName: variant?.name || "",
          colorId,
          colorName: color?.name || "",
        };
      } else items.push({
        ...product,
        variantId,
        variantName: variant?.name || "",
        colorId,
        colorName: color?.name || "",
        compareAtPrice: compareAt,
        price,
        stock,
        quantity,
      });
      return { items };
    });
  }, [token, cart.items]);

  const updateCartItem = useCallback(async (item, quantity) => {
    const productId = item.productId || productIdOf(item);
    if (quantity < 1) return;
    if (quantity > Number(item.stock || 0)) {
      throw new Error(`Only ${item.stock} available in stock.`);
    }
    if (token) {
      const data = await updateServerCartItem(productId, quantity, variantIdOf(item), colorIdOf(item));
      setCart(data.cart || { items: [] });
      return;
    }
    setCart((current) => ({
      items: (current.items || []).map((entry) =>
        cartItemKeyOf(entry) === cartItemKeyOf(item) ? { ...entry, quantity } : entry,
      ),
    }));
  }, [token]);

  const removeCartItem = useCallback(async (item) => {
    const productId = item.productId || productIdOf(item);
    if (token) {
      const data = await removeServerCartItem(productId, variantIdOf(item), colorIdOf(item));
      setCart(data.cart || { items: [] });
      return;
    }
    setCart((current) => ({
      items: (current.items || []).filter((entry) => cartItemKeyOf(entry) !== cartItemKeyOf(item)),
    }));
  }, [token]);

  const clearCart = useCallback(() => {
    setCart({ items: [] });
    localStorage.removeItem(CART_KEY);
    if (token) void clearServerCart().catch(() => {});
  }, [token]);

  const value = useMemo(() => ({
    cart,
    cartSyncing,
    cartItems: cartItemsOf(cart),
    cartCount: cartCount(cart),
    cartSubtotal: cartSubtotal(cart),
    addToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
  }), [cart, cartSyncing, addToCart, updateCartItem, removeCartItem, clearCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used inside CartProvider");
  return context;
}
